import { useCallback, useEffect, useState } from "react";
import { Card, CardBody, EmptyState, Pagination, Skeleton } from "@/ui";
import { KarigarShell } from "@/components/KarigarShell";
import { LedgerList } from "@/components/LedgerList";
import { PointsBalanceCard } from "@/components/PointsBalanceCard";
import { fetchPoints, type PointsPage } from "@/lib/karigarPoints";
import { useLanguage } from "@/i18n/language";

/**
 * Every point in and every point out — WIREFRAMES.md "Karigar history".
 *
 * This is the karigar's own copy of the ledger, and it is the answer to "where
 * did my money go". So it shows the rows as the server stores them — signed,
 * in order, nothing netted off and nothing hidden — rather than a tidier
 * summary that would not survive an argument at a counter.
 */
const PAGE_SIZE = 20;

export function KarigarHistoryPage() {
  const { t } = useLanguage("hi");
  const [data, setData] = useState<PointsPage | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async (p: number): Promise<void> => {
    setLoading(true);
    try {
      setData(await fetchPoints(p, PAGE_SIZE));
      setFailed(false);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(page);
  }, [load, page]);

  return (
    <KarigarShell active="history" title={t("आपका इतिहास", "Your history")}>
      <PointsBalanceCard balance={data?.balance ?? null} loading={loading && !data} />

      <Card>
        <CardBody className="p-0">
          {loading && !data ? (
            <div className="flex flex-col gap-3 p-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : failed ? (
            <EmptyState
              message={t("इंटरनेट देखें और दोबारा कोशिश करें।", "Check your internet and try again.")}
              title={t("अभी जुड़ नहीं पा रहे", "Could not load")}
            />
          ) : data && data.items.length > 0 ? (
            <LedgerList rows={data.items} />
          ) : (
            <EmptyState
              message={t("कोड स्कैन करते ही यहाँ दिखने लगेगा।", "Scan a code and it will appear here.")}
              title={t("अभी कुछ नहीं है", "Nothing yet")}
            />
          )}
        </CardBody>
      </Card>

      {data && data.totalPages > 1 ? (
        <Pagination
          onChange={setPage}
          page={data.page}
          total={data.total}
          totalPages={data.totalPages}
        />
      ) : null}
    </KarigarShell>
  );
}
